/**
 * Runtime configuration loader
 *
 * This module loads configuration from window.__RUNTIME_CONFIG__ which is
 * injected at container startup from Cloud Run environment variables.
 * Falls back to import.meta.env for build-time defaults during development.
 */

interface RuntimeConfig {
  VITE_API_URL: string
  VITE_ENVIRONMENT: string
  VITE_SENTRY_DSN: string
  VITE_DATA_PROJECT_ID: string
  VITE_CLERK_PUBLISHABLE_KEY: string
  VITE_CONFIG_CAT_KEY: string
  VITE_POSTHOG_API_KEY: string
  VITE_POSTHOG_HOST: string
  VITE_GTM_ID: string
  VITE_GTM_URL: string
  VITE_GTM_STAPE_ID: string
  VITE_INTERCOM_WORKSPACE_ID: string
  VITE_INTERCOM_SECRET: string
  VITE_COSTORY_ORGID: string
  VITE_COSTORY_EMAIL_DOMAIN: string
}

// Declare window.__RUNTIME_CONFIG__ type
declare global {
  interface Window {
    __RUNTIME_CONFIG__?: Partial<RuntimeConfig>
  }
}

/**
 * Get a configuration value from runtime config or fallback to build-time env
 */
function getConfig<K extends keyof RuntimeConfig>(
  key: K,
  defaultValue: string = "",
): RuntimeConfig[K] {
  // Try runtime config first (from Cloud Run env vars)
  if (typeof window !== "undefined" && window.__RUNTIME_CONFIG__) {
    const runtimeValue = window.__RUNTIME_CONFIG__[key]
    if (runtimeValue !== undefined && runtimeValue !== "") {
      return runtimeValue as RuntimeConfig[K]
    }
  }

  // Fallback to build-time env (for local development)
  const buildTimeValue = import.meta.env[key]
  if (buildTimeValue !== undefined) {
    return buildTimeValue as RuntimeConfig[K]
  }

  // Return default value
  return defaultValue as RuntimeConfig[K]
}

/**
 * Runtime configuration object
 * Use this instead of import.meta.env.VITE_* for production
 */
export const runtimeConfig = {
  get VITE_API_URL() {
    return getConfig("VITE_API_URL")
  },
  get VITE_ENVIRONMENT() {
    return getConfig("VITE_ENVIRONMENT", "production")
  },
  get VITE_SENTRY_DSN() {
    return getConfig("VITE_SENTRY_DSN")
  },
  get VITE_DATA_PROJECT_ID() {
    return getConfig("VITE_DATA_PROJECT_ID")
  },
  get VITE_CLERK_PUBLISHABLE_KEY() {
    return getConfig("VITE_CLERK_PUBLISHABLE_KEY")
  },
  get VITE_CONFIG_CAT_KEY() {
    return getConfig("VITE_CONFIG_CAT_KEY")
  },
  get VITE_POSTHOG_API_KEY() {
    return getConfig("VITE_POSTHOG_API_KEY")
  },
  get VITE_POSTHOG_HOST() {
    return getConfig("VITE_POSTHOG_HOST", "https://eu.i.posthog.com")
  },
  get VITE_GTM_ID() {
    return getConfig("VITE_GTM_ID")
  },
  get VITE_GTM_URL() {
    return getConfig("VITE_GTM_URL")
  },
  get VITE_GTM_STAPE_ID() {
    return getConfig("VITE_GTM_STAPE_ID")
  },
  get VITE_INTERCOM_WORKSPACE_ID() {
    return getConfig("VITE_INTERCOM_WORKSPACE_ID")
  },
  get VITE_INTERCOM_SECRET() {
    return getConfig("VITE_INTERCOM_SECRET")
  },
  get VITE_COSTORY_ORGID() {
    return getConfig("VITE_COSTORY_ORGID")
  },
  get VITE_COSTORY_EMAIL_DOMAIN() {
    return getConfig("VITE_COSTORY_EMAIL_DOMAIN")
  },
}
